import { cssFontFamily } from './_fonts';
import { menuControlFor } from './_get_menu_control';

// Syncs fontSize, fontFamily, lineHeight, and color from uniform textStyle marks
// onto the list node itself, so CSS pseudo-elements (bullets/numbers)
// can inherit those styles.
export const syncListStyles = (nodeTypeName) => {
  return ({ editor, transaction }) => {
    if (!transaction.docChanged) {
      return;
    }

    const { state } = editor;
    const { from, to } = state.selection;

    // Collect all updates first, then apply in a single transaction
    const updates = [];

    state.doc.nodesBetween(from, to, (node, pos) => {
      if (node.type.name !== nodeTypeName) {
        return;
      }

      let fontSize = undefined;
      let fontFamily = undefined;
      let lineHeight = undefined;
      let color = undefined;
      let uniform = true;

      // Check if all textStyle marks in the list share the same values
      node.descendants((child) => {
        if (!uniform) {
          return false;
        }
        const mark = child.marks.find((m) => m.type.name === 'textStyle');
        if (!mark) {
          return;
        }

        if (fontSize === undefined) {
          fontSize = mark.attrs.fontSize;
          fontFamily = mark.attrs.fontFamily;
          lineHeight = mark.attrs.lineHeight;
          color = mark.attrs.color;
        } else if (
          fontSize !== mark.attrs.fontSize ||
          fontFamily !== mark.attrs.fontFamily ||
          lineHeight !== mark.attrs.lineHeight ||
          color !== mark.attrs.color
        ) {
          uniform = false;
        }
      });

      // No textStyle marks found (e.g. empty list) — keep existing
      // node attrs rather than resetting them to null.
      if (fontSize === undefined) {
        return;
      }

      if (!uniform) {
        return;
      }

      fontSize = fontSize ?? null;
      fontFamily = fontFamily ?? null;
      lineHeight = lineHeight ?? null;
      color = color ?? null;

      if (
        fontSize !== node.attrs.fontSize ||
        fontFamily !== node.attrs.fontFamily ||
        lineHeight !== node.attrs.lineHeight ||
        color !== node.attrs.color
      ) {
        updates.push({ pos, attrs: { ...node.attrs, fontSize, fontFamily, lineHeight, color } });
      }
    });

    if (updates.length === 0) {
      return;
    }

    // Apply all updates in a single transaction to avoid mismatched state.
    // Preserve any storedMarks that exist on the current state — setNodeMarkup
    // adds a step which clears tr.storedMarks, so we restore them afterward.
    const tr = state.tr;
    const savedStoredMarks = state.storedMarks;
    for (const { pos, attrs } of updates) {
      tr.setNodeMarkup(pos, undefined, attrs);
    }
    if (savedStoredMarks) {
      tr.setStoredMarks(savedStoredMarks);
    }
    editor.view.dispatch(tr);
  };
};

// Returns a syncMenu handler that toggles the 'active' class on the
// matching toolbar control.
export const makeSyncMenu = (listType, menuVariant) => {
  return ({ editor }) => {
    const control = menuControlFor(editor, 'list:toggle', menuVariant);
    const controlIsActive = editor.isActive(listType);

    control?.classList?.toggle('active', controlIsActive);
  };
};

// Style attributes (fontSize, fontFamily, lineHeight, color) shared by both
// bulletList and orderedList nodes.
export const listStyleAttributes = {
  fontSize: {
    default: null,
    renderHTML: (it) => {
      if (!it.fontSize) {
        return {};
      }
      return { style: `font-size: ${it.fontSize}` };
    },
  },
  fontFamily: {
    default: null,
    renderHTML: (it) => {
      if (!it.fontFamily) {
        return {};
      }
      return { style: `font-family: ${cssFontFamily(it.fontFamily)}` };
    },
  },
  lineHeight: {
    default: null,
    renderHTML: (it) => {
      if (!it.lineHeight) {
        return {};
      }
      return { style: `line-height: ${it.lineHeight}` };
    },
  },
  color: {
    default: null,
    renderHTML: (it) => {
      if (!it.color) {
        return {};
      }
      return { style: `color: ${it.color}` };
    },
  },
};

// Returns an addCommands() config that toggles the list while
// propagating current textStyle attributes.
export const makeToggleListCommand = (commandName) => {
  return function () {
    return {
      [commandName]:
        () =>
        ({ chain }) => {
          const styles = this.editor.getAttributes('textStyle');
          return chain()
            .toggleList(this.name, this.options.itemTypeName, this.options.keepMarks, styles)
            .updateAttributes('listItem', styles)
            .updateAttributes(this.name, styles)
            .command(({ tr, state }) => {
              // toggleList's structural changes (wrapInList, join) clear
              // storedMarks. When the paragraph was empty, the user's next
              // keystroke would produce unstyled text. Re-apply the captured
              // styles as storedMarks so typed text inherits them.
              const { empty, $from } = tr.selection;
              if (empty && $from.parent.textContent.length === 0 && styles.fontSize) {
                const markType = state.schema.marks.textStyle;
                if (markType) {
                  tr.setStoredMarks([
                    markType.create({
                      fontFamily: styles.fontFamily || null,
                      fontSize: styles.fontSize || null,
                      lineHeight: styles.lineHeight || null,
                      color: styles.color || null,
                    }),
                  ]);
                }
              }
              return true;
            })
            .run();
        },
    };
  };
};
